import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { trackEvent } from 'src/app/core/analytics';

export type LoginChallenge = {
  salt: string;
  nonce: string;
  challengeId: string;
};

/**
 * Monitor Mode State
 * Tracks if user is in read-only monitoring mode
 */
export interface MonitorModeState {
  isMonitoring: boolean;
  tokenType: 'full' | 'readonly';
  monitorToken?: string;
  since?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {

  // Observable for monitor mode state changes
  public monitorMode$ = new BehaviorSubject<MonitorModeState>({
    isMonitoring: false,
    tokenType: 'full',
  });

  public loginChallenge(email: string): Observable<LoginChallenge> {
    console.log('[AuthService] Requesting login challenge for:', email);
    return this.request(
      {
        path: 'users/login-challenge',
        method: 'GET',
        query: { email },
        noAuth: true,
      },
      { withCredentials: true },
    ).pipe(
      tap((result) => {
        console.log(
          '[AuthService] Received login challenge - nonce:',
          result.nonce?.substring(0, 10) + '...',
        );
      }),
      catchError((err) => {
        console.error('[AuthService] Login challenge failed:', err);
        return throwError(() => err);
      }),
    );
  }

  public loginWithProof(payload: {
    email: string;
    proof: string;
    challengeId: string;
  }): Observable<any> {
    console.log(
      '[AuthService] Attempting login with proof for:',
      payload.email,
    );
    return this.request(
      {
        path: 'users/login',
        method: 'POST',
        body: payload,
        noAuth: true,
      },
      { withCredentials: true },
    ).pipe(
      tap((result) => {
        console.log('[AuthService] Login successful - user:', result.email);
      }),
      catchError((err) => {
        console.error('[AuthService] Login failed:', err.error?.message);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Request Read-Only Monitor Token
   * Returns a 24-hour read-only token for monitoring online tests
   * Used when user enters "Monitor Mode"
   */
  public requestMonitorToken(): Observable<{
    token: string;
    expiresIn: string;
  }> {
    console.log('[AuthService] Requesting read-only monitor token...');
    return this.request({
      path: 'users/monitor-token',
      method: 'POST',
      body: {},
    }).pipe(
      tap((result) => {
        console.log(
          '[AuthService] Received read-only monitor token - expires in:',
          result.expiresIn,
        );

        // Store monitor token in localStorage for 24h persistence across page refreshes
        localStorage.setItem('monitor_token', result.token);

        trackEvent('monitor_mode_entered', {});

        // Update monitor mode state
        this.monitorMode$.next({
          isMonitoring: true,
          tokenType: 'readonly',
          monitorToken: result.token,
          since: new Date(),
        });
      }),
      catchError((err) => {
        console.error('[AuthService] Monitor token request failed:', err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Step-Up Authentication
   * Verifies password and returns full access token
   * Used when user tries to perform write action in read-only mode
   *
   * One call with the password as typed. Which password it is checked
   * against is the server's decision, not this one's.
   */
  public stepUpAuthentication(
    password: string,
  ): Observable<{ token: string; expiresIn: string }> {
    console.log('[AuthService] Requesting step-up authentication...');
    return this.request({
      path: 'users/step-up',
      method: 'POST',
      body: { password },
    }).pipe(
      tap((result) => {
        console.log('[AuthService] Step-up authentication successful');

        if (!result?.token) {
          throw new Error('No token received from step-up authentication');
        }

        this.storageBrowser.set('token', result.token);
        this.storageBrowser.set('last_active', Date.now());

        // How long the monitoring session lasted, measured from when the
        // read-only token was issued.
        const since = this.monitorMode$.value?.since;
        trackEvent('monitor_mode_exited', {
          duration_seconds: since
            ? Math.round((Date.now() - new Date(since).getTime()) / 1000)
            : undefined,
        });

        this.monitorMode$.next({
          isMonitoring: false,
          tokenType: 'full',
        });

        this.storageBrowser.remove('monitor_token');
      }),
      catchError((err) => {
        console.error(
          '[AuthService] Step-up authentication failed:',
          err.error?.message,
        );
        return throwError(() => err);
      }),
    );
  }

  /**
   * Check if currently in monitor mode
   */
  public isInMonitorMode(): boolean {
    return this.monitorMode$.value.isMonitoring;
  }

  /**
   * Get current monitor token if active
   */
  public getMonitorToken(): string | null {
    if (this.isInMonitorMode()) {
      return localStorage.getItem('monitor_token');
    }
    return null;
  }

  public forgotpswd(params: { email: any }): Observable<any> {
    return this.request({
      path: 'users/password-reset',
      method: 'POST',
      body: params,
      noAuth: true,
    });
  }

  public acceptTerms(params: { token: string }): Observable<any> {
    const headers = {
      Authorization: `Bearer ${params.token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    return this.request(
      {
        path: 'users/accept-terms',
        method: 'POST',
        noAuth: true,
      },
      { headers },
    );
  }

  /**
   * Asks whether a reset link is still redeemable before the set-password form
   * is shown, so a tampered-with or stale link is refused on arrival. Checking
   * neither consumes nor extends the link.
   */
  public validatePasswordResetToken(
    token: string,
  ): Observable<{ valid: boolean }> {
    return this.request({
      path: 'users/password-reset-token/validate',
      method: 'POST',
      body: { token },
      noAuth: true,
    });
  }

  public changePassword(params: {
    password: any;
    token: any;
  }): Observable<any> {
    return this.request({
      path: 'users/password-change',
      method: 'POST',
      body: params,
      noAuth: true,
    });
  }

  public me(): Observable<any> {
    return this.request({
      path: 'users/me',
      method: 'GET',
    });
  }

  /**
   * End the session on the server as well as in this tab.
   *
   * Clearing local storage only removes this browser's copy of the access
   * token. The refresh token is an HttpOnly cookie plus a row in the database,
   * neither of which JavaScript can touch — so without this call the session
   * survives logging out, and the next person to open the app on the same
   * machine gets silently refreshed back into it.
   *
   * `withCredentials` because the cookie is the whole credential here, and
   * `noAuth` because someone logging out may be holding an access token that
   * has already expired. Refusing them would leave the session alive, which is
   * the situation this exists to prevent.
   */
  public logout(): Observable<{ status: string }> {
    return this.request(
      {
        path: 'users/logout',
        method: 'POST',
        body: {},
        noAuth: true,
      },
      { withCredentials: true },
    );
  }

  public getCrossSessionToken(): Observable<{ cst: string }> {
    return this.request({
      path: 'sso/cross-session-token',
      method: 'POST',
      body: {},
    });
  }

  // Single-flight guard: the refresh token is rotated server-side on each
  // successful refresh, so two concurrent refresh calls would make one revoke
  // the other ("Refresh Token Invalid or Revoked"). Both the interceptor's
  // reactive 401 refresh and the SessionService proactive refresh go through
  // here, so they must share one in-flight request. See .
  private refreshInFlight$: Observable<any> | null = null;

  public refreshToken(): Observable<any> {
    if (this.refreshInFlight$) {
      console.log('[AuthService] Reusing in-flight token refresh');
      return this.refreshInFlight$;
    }

    console.log('[AuthService] Requesting silent token refresh...');

    this.refreshInFlight$ = this.request(
      {
        path: 'users/refresh-token',
        method: 'POST',
        body: {},
        noAuth: true,
      },
      { withCredentials: true },
    ).pipe(
      tap((result) => {
        console.log('[AuthService] Silent token refresh successful');
      }),
      catchError((err) => {
        console.error('[AuthService] Silent token refresh failed:', {
          status: err.status,
          message: err.error?.message,
        });
        return throwError(() => err);
      }),
      // Release the guard once the request settles so the next refresh runs anew.
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshInFlight$;
  }
}
